import { useMemo, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/components/base/Toast";
import Select from "@/components/base/Select";
import { useStore } from "@/context/StoreContext";
import type { BranchDetail } from "@/types";

interface FormState {
  name: string;
  city: string;
  address: string;
  phone: string;
  managerId: string;
  hours: string;
  carPickup: boolean;
  localPickup: boolean;
  deliveryEnabled: boolean;
  deliveryFee: string;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

const EMPTY: FormState = {
  name: "",
  city: "الرياض",
  address: "",
  phone: "",
  managerId: "",
  hours: "١١:٠٠ ص — ١٢:٠٠ ص",
  carPickup: true,
  localPickup: false,
  deliveryEnabled: false,
  deliveryFee: "15.00",
};

export default function BranchFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { show } = useToast();
  const { branches, employees, saveBranch } = useStore();

  const isEdit = Boolean(id);
  const editing = isEdit
    ? (branches.find((branch) => branch.id === id) ?? null)
    : null;

  const [form, setForm] = useState<FormState>(() =>
    editing
      ? {
          name: editing.name,
          city: editing.city,
          address: editing.address,
          phone: editing.phone,
          managerId: editing.managerId ?? "",
          hours: editing.hours,
          carPickup: editing.carPickup,
          localPickup: editing.localPickup,
          deliveryEnabled: editing.deliveryEnabled,
          deliveryFee: editing.deliveryFee.toFixed(2),
        }
      : EMPTY,
  );
  const [error, setError] = useState("");

  const branchEmployees = useMemo(
    () => employees.filter((employee) => employee.branchId === editing?.id),
    [employees, editing],
  );

  const branchManagers = useMemo(
    () =>
      branchEmployees.filter(
        (employee) =>
          employee.role === "branchManager" && employee.status === "active",
      ),
    [branchEmployees],
  );

  if (isEdit && !editing) {
    return (
      <div className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card px-8 py-20 text-center">
        <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-store-3-line text-3xl"></i>
        </div>
        <h2 className="text-lg font-bold text-foreground-950">
          الفرع غير موجود
        </h2>
        <p className="mt-1 text-sm text-foreground-500">
          تعذّر العثور على الفرع المطلوب، ربما تم حذفه.
        </p>
        <button
          type="button"
          onClick={() => navigate("/branches")}
          className="mt-5 inline-flex items-center gap-2 rounded-btn bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-line"></i>
          العودة للفروع
        </button>
      </div>
    );
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!form.name.trim() || !form.address.trim()) {
      setError("الرجاء إدخال اسم الفرع والعنوان");
      return;
    }
    const selectedManager = branchManagers.find(
      (employee) => employee.id === form.managerId,
    );
    const branch: BranchDetail = {
      id: editing?.id ?? `b${Date.now()}`,
      name: form.name.trim(),
      city: form.city.trim() || "الرياض",
      address: form.address.trim(),
      phone: form.phone.trim(),
      manager: selectedManager?.name ?? "",
      managerId: selectedManager?.id ?? "",
      hours: form.hours.trim(),
      staffCount: branchEmployees.length,
      isOpen: editing?.isOpen ?? false,
      isActive: editing?.isActive ?? true,
      ordersToday: editing?.ordersToday ?? 0,
      salesToday: editing?.salesToday ?? 0,
      carPickup: form.carPickup,
      localPickup: form.localPickup,
      deliveryEnabled: form.deliveryEnabled,
      deliveryFee: Math.max(round2(Number(form.deliveryFee) || 0), 0),
    };
    saveBranch(branch);
    show(
      editing
        ? "تم تحديث إعدادات التوصيل ورسومه بنجاح."
        : "تمت إضافة الفرع الجديد",
    );
    navigate("/branches");
  };

  const toggleSwitch = (
    key: "carPickup" | "localPickup" | "deliveryEnabled",
  ) => setForm((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="space-y-4">
      <div className="yc-fade-up flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/branches")}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
          aria-label="رجوع"
        >
          <i className="ri-arrow-right-line text-xl"></i>
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-foreground-950">
            {editing ? "تعديل بيانات الفرع" : "إضافة فرع جديد"}
          </h2>
          <p className="mt-1 text-sm text-foreground-500">
            {editing
              ? "عدّل بيانات الفرع ثم احفظ التغييرات"
              : "أدخل بيانات الفرع لإضافته للقائمة"}
          </p>
        </div>
        {editing && (
          <button
            type="button"
            onClick={() => navigate(`/employees?branch=${editing.id}`)}
            className="inline-flex items-center gap-2 h-11 rounded-btn bg-accent-100 px-4 text-sm font-semibold text-accent-900 hover:bg-accent-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-team-line"></i>
            إدارة موظفي الفرع
          </button>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card p-5 md:p-6"
      >
        <div className="max-w-2xl space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                اسم الفرع <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
                placeholder="مثال: فرع العليا"
                className="w-full h-11 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                المدينة
              </label>
              <input
                type="text"
                value={form.city}
                onChange={(event) =>
                  setForm({ ...form, city: event.target.value })
                }
                className="w-full h-11 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-2">
              العنوان <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.address}
              onChange={(event) =>
                setForm({ ...form, address: event.target.value })
              }
              placeholder="الحي، الشارع، المدينة"
              className="w-full h-11 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                هاتف الفرع
              </label>
              <input
                type="tel"
                dir="ltr"
                value={form.phone}
                onChange={(event) =>
                  setForm({ ...form, phone: event.target.value })
                }
                placeholder="0112345678"
                className="w-full h-11 rounded-btn border border-background-200 px-3 text-left text-sm outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                عدد الموظفين
              </label>
              <div className="flex items-center gap-2 h-11 rounded-btn border border-background-200 bg-background-50 px-3">
                <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                  <i className="ri-team-line"></i>
                </span>
                <span className="text-sm font-medium text-foreground-700">
                  {branchEmployees.length} موظف
                </span>
                <span className="ms-auto text-xs text-foreground-400">
                  يُحسب تلقائيًا
                </span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-2">
              مدير الفرع
            </label>
            {!isEdit ? (
              <p className="rounded-btn border border-dashed border-background-300 bg-background-50 px-4 py-3 text-xs text-foreground-500">
                أضف الفرع أولًا، ثم عيّن مدير الفرع من حسابات الموظفين.
              </p>
            ) : branchManagers.length === 0 ? (
              <div className="flex flex-col gap-3 rounded-btn border border-dashed border-background-300 bg-background-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600">
                  <i className="ri-user-star-line text-foreground-400"></i>
                  لا يوجد مدير فرع مضاف
                </span>
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/employees/new?branch=${editing?.id}`)
                  }
                  className="inline-flex items-center justify-center gap-2 h-10 rounded-btn bg-primary-500 px-4 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-user-add-line"></i>
                  إضافة مدير فرع
                </button>
              </div>
            ) : (
              <Select
                value={form.managerId}
                options={[
                  { value: "", label: "بدون مدير" },
                  ...branchManagers.map((employee) => ({
                    value: employee.id,
                    label: employee.name,
                  })),
                ]}
                onChange={(value) => setForm({ ...form, managerId: value })}
                placeholder="بدون مدير"
                icon="ri-user-star-line"
                ariaLabel="مدير الفرع"
              />
            )}
            <p className="mt-2 flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-information-line"></i>
              تُعرض قائمة مدير الفرع فقط للموظفين النشطين المرتبطين بهذا الفرع
              ويحملون دور «مدير فرع».
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-2">
              أوقات العمل
            </label>
            <input
              type="text"
              value={form.hours}
              onChange={(event) =>
                setForm({ ...form, hours: event.target.value })
              }
              className="w-full h-11 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>

          <div className="rounded-box border border-background-200 p-4">
            <h3 className="text-sm font-bold text-foreground-950 mb-1">
              طرق الاستلام
            </h3>
            <p className="mb-3 text-xs text-foreground-500">
              تحكّم في الطرق المتاحة للعملاء داخل هذا الفرع مع إعدادات كل طريقة.
            </p>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between rounded-btn border border-background-200 px-4 py-3">
                <span className="inline-flex items-center gap-3 text-sm font-medium text-foreground-800">
                  <span className="w-6 h-6 flex items-center justify-center text-foreground-500">
                    <i className="ri-store-3-line"></i>
                  </span>
                  استلام من داخل الفرع
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-[11px] font-semibold text-primary-800 whitespace-nowrap">
                  <i className="ri-checkbox-circle-fill"></i>
                  متاح دائمًا
                </span>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={form.carPickup}
                onClick={() => toggleSwitch("carPickup")}
                className="w-full flex items-center justify-between rounded-btn border border-background-200 px-4 py-3 cursor-pointer"
              >
                <span className="inline-flex items-center gap-3 text-sm font-medium text-foreground-800">
                  <span className="w-6 h-6 flex items-center justify-center text-foreground-500">
                    <i className="ri-car-line"></i>
                  </span>
                  استلام من السيارة
                </span>
                <span
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                    form.carPickup ? "bg-primary-500" : "bg-background-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-200 ${
                      form.carPickup ? "start-5" : "start-0.5"
                    }`}
                  ></span>
                </span>
              </button>

              <button
                type="button"
                role="switch"
                aria-checked={form.localPickup}
                onClick={() => toggleSwitch("localPickup")}
                className="w-full flex items-center justify-between rounded-btn border border-background-200 px-4 py-3 cursor-pointer"
              >
                <span className="inline-flex items-center gap-3 text-sm font-medium text-foreground-800">
                  <span className="w-6 h-6 flex items-center justify-center text-foreground-500">
                    <i className="ri-restaurant-2-line"></i>
                  </span>
                  طلب محلي داخل الفرع
                </span>
                <span
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                    form.localPickup ? "bg-primary-500" : "bg-background-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-200 ${
                      form.localPickup ? "start-5" : "start-0.5"
                    }`}
                  ></span>
                </span>
              </button>

              <div className="rounded-btn border border-background-200 overflow-hidden">
                <div className="flex flex-wrap items-center gap-2 bg-background-50 px-4 py-3">
                  <span className="w-6 h-6 flex items-center justify-center text-foreground-500">
                    <i className="ri-e-bike-2-line"></i>
                  </span>
                  <span className="text-sm font-bold text-foreground-900">
                    التوصيل بواسطة المحل
                  </span>
                  <span className="ms-auto inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-900 whitespace-nowrap">
                    <i className="ri-lock-line"></i>
                    لصاحب المحل التجاري فقط
                  </span>
                </div>

                <div className="space-y-3 px-4 py-3.5">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.deliveryEnabled}
                    onClick={() => toggleSwitch("deliveryEnabled")}
                    className="w-full flex items-center justify-between cursor-pointer"
                  >
                    <span className="inline-flex items-center gap-3 text-sm font-medium text-foreground-800">
                      <span className="w-6 h-6 flex items-center justify-center text-foreground-500">
                        <i className="ri-toggle-line"></i>
                      </span>
                      تفعيل التوصيل لهذا الفرع
                    </span>
                    <span
                      className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                        form.deliveryEnabled
                          ? "bg-primary-500"
                          : "bg-background-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-200 ${
                          form.deliveryEnabled ? "start-5" : "start-0.5"
                        }`}
                      ></span>
                    </span>
                  </button>

                  <p className="flex items-start gap-2 text-xs text-foreground-500 leading-6">
                    <i className="ri-information-line mt-0.5"></i>
                    <span>
                      يتولى المحل مسؤولية توصيل الطلب، وتقوم منصة يور كاشير
                      بتحصيل الرسوم فقط.
                    </span>
                  </p>

                  {form.deliveryEnabled && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 rounded-btn bg-background-50 border border-background-200 px-4 py-3">
                        <span className="text-sm font-medium text-foreground-700">
                          رسوم التوصيل الثابتة
                        </span>
                        <div className="ms-auto inline-flex items-center gap-2 h-10 rounded-btn border border-background-200 bg-white px-3 focus-within:ring-2 focus-within:ring-primary-300">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            dir="ltr"
                            value={form.deliveryFee}
                            onChange={(event) => {
                              const value = event.target.value;
                              if (value !== "" && Number(value) < 0) return;
                              setForm({ ...form, deliveryFee: value });
                            }}
                            onBlur={() =>
                              setForm((prev) => ({
                                ...prev,
                                deliveryFee: Math.max(
                                  round2(Number(prev.deliveryFee) || 0),
                                  0,
                                ).toFixed(2),
                              }))
                            }
                            className="w-20 h-full bg-transparent text-left text-sm outline-none"
                          />
                          <span className="text-xs font-medium text-foreground-500">
                            ر.س
                          </span>
                        </div>
                      </div>
                      <p className="flex items-start gap-2 text-[11px] text-foreground-500 leading-6">
                        <i className="ri-time-line mt-0.5"></i>
                        <span>
                          تظهر رسوم التوصيل للعميل أثناء إتمام الطلب، وتُطبّق
                          التعديلات على الطلبات الجديدة فقط.
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        <div className="mt-6 flex flex-wrap gap-3 border-t border-background-100 pt-5">
          <button
            type="button"
            onClick={() => navigate("/branches")}
            className="h-11 px-6 rounded-btn bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            إلغاء
          </button>
          <button
            type="submit"
            className="h-11 px-6 rounded-btn bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {editing ? "حفظ التعديلات" : "إضافة الفرع"}
          </button>
        </div>
      </form>
    </div>
  );
}